import { ChevronDown } from 'lucide-react';
import { FloatingLabel, FormFieldShell } from './FormFieldShell';
import { getFloatingFieldWrapperClass, useFloatingLabelState } from './floatingLabel.utils';
import {
  getFloatingSelectClass,
  getStackedSelectClass,
} from './formField.styles';
import type { FormSelectFieldProps } from './formField.types';
import { useFormFieldVariant } from './useFormFieldVariant';

export function FormSelectField({
  label,
  error,
  required = true,
  variant,
  className,
  options,
  placeholder = 'Select',
  disabled = false,
  value,
  onFocus,
  onBlur,
  ...props
}: FormSelectFieldProps) {
  const resolvedVariant = useFormFieldVariant(variant);
  const hasError = Boolean(error);
  const stringValue = value == null ? '' : String(value);
  const isFilled = stringValue.length > 0;
  const { isLabelActive, isFocused, mergeFocusHandlers } = useFloatingLabelState(isFilled);

  if (resolvedVariant === 'floating') {
    return (
      <FormFieldShell variant="floating" label={label} error={error} required={required} className={className}>
        <div className={getFloatingFieldWrapperClass(isFilled, disabled, hasError, isFocused)}>
          <FloatingLabel label={label} isActive={isLabelActive} />
          <select
            value={value}
            disabled={disabled}
            className={getFloatingSelectClass()}
            aria-invalid={hasError}
            aria-label={label}
            {...mergeFocusHandlers(onFocus, onBlur)}
            {...props}
          >
            <option value="" />
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#808080]"
            aria-hidden
          />
        </div>
      </FormFieldShell>
    );
  }

  return (
    <FormFieldShell variant="stacked" label={label} error={error} required={required} className={className}>
      <select
        value={value}
        disabled={disabled}
        className={getStackedSelectClass(hasError)}
        aria-invalid={hasError}
        onFocus={onFocus}
        onBlur={onBlur}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FormFieldShell>
  );
}
